import { useTranslation } from "react-i18next";
import { MONEY_PRIVACY_CLASS } from "../../../utils/moneyPrivacy";
import { toMxn, type FxRates } from "../lib/ledger";
import { money } from "./ExpectedKpis";

// ---------------------------------------------------------------------------
// Equivalente en MXN de lo que se paga en otra moneda. El desglose del tablero
// lo pinta SIEMPRE, en todas sus pestañas: un importe en USD va con sus pesos
// debajo, al tipo de cambio de la plataforma (el mismo de Finanzas). Si no hay
// tipo de cambio se dice; nunca se deja el importe extranjero solo y callado.
// ---------------------------------------------------------------------------

interface Props {
  amount: number;
  currency: string;
  fx: FxRates | null | undefined;
  className?: string;
  testId?: string;
}

/** «≈ $1,850 MXN» bajo un importe en otra moneda. Nada si el importe ya está en MXN. */
export function MxnEquivalent({ amount, currency, fx, className = "", testId }: Props) {
  const { t, i18n } = useTranslation("common");
  if (currency === "MXN") return null;
  const mxn = toMxn(amount, currency, fx);
  if (mxn === null) {
    return (
      <span className={`block whitespace-nowrap text-[11px] font-normal text-amber-600`} data-testid={testId} data-fx-missing>
        {t("workHours.fx.noRate")}
      </span>
    );
  }
  return (
    <span className={`block whitespace-nowrap text-[11px] font-normal ${MONEY_PRIVACY_CLASS} ${className}`} data-testid={testId}>
      ≈ {money(mxn, "MXN", i18n?.language)}
    </span>
  );
}

/** Con qué tipo de cambio se convirtió (y qué moneda se quedó sin convertir). */
export function FxNote({ fx, fxMissing, className = "" }: { fx: FxRates | null | undefined; fxMissing?: string[] | null; className?: string }) {
  const { t, i18n } = useTranslation("common");
  const codes = Object.keys(fx ?? {}).sort();
  const missing = fxMissing ?? [];
  if (!codes.length && !missing.length) return null;
  const rateLabel = (rate: number) =>
    new Intl.NumberFormat(String(i18n?.language).startsWith("en") ? "en-US" : "es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(rate);
  return (
    <p className={`mt-2 text-[11px] ${className}`} data-testid="breakdown-fx-note">
      {codes.map((c) => {
        const r = (fx as FxRates)[c];
        const origin = [r.source, r.date].filter(Boolean).join(", ");
        return (
          <span key={c} className="mr-2 inline-block">
            {t("workHours.fx.rate", { currency: c, rate: rateLabel(Number(r.rate)) })}
            {origin ? ` (${origin})` : ""}
          </span>
        );
      })}
      {missing.length > 0 && (
        <span className="inline-block text-amber-600" data-testid="breakdown-fx-missing">
          {t("workHours.fx.missing", { currencies: missing.join(", ") })}
        </span>
      )}
    </p>
  );
}
